import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  Mail,
  X,
  Send,
  Minimize2,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import { useAuth } from "@/auth/useAuth";
import { cn } from "@/lib/utils";
import { useEmail } from "@/providers/EmailProvider";
import { sendEmail } from "@/services/graphServices/sendEmail";

interface SendEmailProps {
  variant?: "dialog" | "popup";
}

export const SendEmail = ({ variant = "dialog" }: SendEmailProps) => {
  const { isOpen, closeEmail, emailData } = useEmail();
  const { getToken } = useAuth();
  const [minimized, setMinimized] = useState(false);

  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  // Update fields when emailData changes
  useEffect(() => {
    if (emailData) {
      setTo(emailData.to || "");
      setSubject(emailData.subject || "");
      setBody(emailData.body || "");
    }
  }, [emailData]);

  // Reset status when dialog opens
  useEffect(() => {
    if (isOpen) {
      setStatus("idle");
      setErrorMessage("");
    }
  }, [isOpen]);

  const handleSend = async () => {
    if (!to || !subject) {
      setStatus("error");
      setErrorMessage("Please fill in recipient and subject");
      return;
    }

    setSending(true);
    setStatus("idle");
    setErrorMessage("");

    try {
      const token = await getToken();
      if (!token) {
        throw new Error("Failed to get authentication token");
      }

      await sendEmail(token, {
        to: to.split(",").map((email) => email.trim()),
        subject,
        body,
        bodyType: "text",
      });

      setStatus("success");

      // Close after 2 seconds on success
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (error) {
      console.error("Error sending email:", error);
      setStatus("error");
      setErrorMessage(
        error instanceof Error ? error.message : "Failed to send email"
      );
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setMinimized(false);
    setStatus("idle");
    setErrorMessage("");
    // Clear fields on close
    setTo("");
    setSubject("");
    setBody("");
    closeEmail();
  };

  const startRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (isOpen) {
      // Use RAF to ensure DOM is mounted inside the Dialog
      requestAnimationFrame(() => {
        startRef.current?.focus();
      });
    }
  }, [isOpen]);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      handleClose();
    }
  };
  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Popup variant (bottom right corner)
  if (variant === "popup") {
    return (
      <div
        className={cn(
          "fixed bottom-4 right-4 w-96 bg-card border border-border rounded-lg shadow-2xl transition-all duration-300 z-50",
          isOpen
            ? "translate-y-0 opacity-100"
            : "translate-y-full opacity-0 pointer-events-none",
          minimized && "h-14"
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <Mail className="size-4 text-primary" />
            <span className="text-sm font-semibold">New Message</span>
          </div>
          <div className="flex items-center gap-1">
            <IconButton
              size="small"
              onClick={() => setMinimized(!minimized)}
              sx={{ color: "text.secondary" }}
            >
              <Minimize2 className="size-3.5" />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleClose}
              sx={{ color: "text.secondary" }}
            >
              <X className="size-3.5" />
            </IconButton>
          </div>
        </div>

        {/* Content */}
        {!minimized && (
          <div className="p-4 space-y-3 max-h-96 overflow-auto">
            {/* Status Messages */}
            {status === "success" && (
              <Alert
                severity="success"
                icon={<CheckCircle className="size-4" />}
                className="mb-2"
              >
                Email sent successfully!
              </Alert>
            )}
            {status === "error" && (
              <Alert
                severity="error"
                icon={<AlertCircle className="size-4" />}
                className="mb-2"
              >
                {errorMessage}
              </Alert>
            )}

            <TextField
              fullWidth
              size="small"
              label="To"
              placeholder="recipient@example.com"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              variant="outlined"
              disabled={sending}
              error={status === "error" && !to}
            />
            <TextField
              fullWidth
              size="small"
              label="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              variant="outlined"
              disabled={sending}
              error={status === "error" && !subject}
            />
            <TextField
              fullWidth
              multiline
              inputRef={startRef}
              rows={6}
              label="Message"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              variant="outlined"
              disabled={sending}
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outlined"
                size="small"
                onClick={handleClose}
                disabled={sending}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={
                  sending ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <Send className="size-3.5" />
                  )
                }
                onClick={handleSend}
                disabled
              >
                {sending ? "Sending..." : "Send"}
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Dialog variant (centered modal)
  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 24,
        },
      }}
    >
      <div className="relative">
        <DialogTitle className="flex items-center gap-2 pr-12">
          <Mail className="size-5" />
          <span>New Message</span>
        </DialogTitle>
        <IconButton
          size="small"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "text.secondary",
          }}
        >
          <X className="size-4" />
        </IconButton>
      </div>

      <DialogContent>
        <div className="space-y-3 pt-2">
          {/* Status Messages */}
          {status === "success" && (
            <Alert
              severity="success"
              icon={<CheckCircle className="size-4" />}
              className="mb-2"
            >
              Email sent successfully!
            </Alert>
          )}
          {status === "error" && (
            <Alert
              severity="error"
              icon={<AlertCircle className="size-4" />}
              className="mb-2"
            >
              {errorMessage}
            </Alert>
          )}

          <TextField
            fullWidth
            size="small"
            label="To"
            placeholder="recipient@example.com"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            variant="outlined"
            disabled={sending}
            error={status === "error" && !to}
            helperText="Separate multiple emails with commas"
          />
          <TextField
            fullWidth
            size="small"
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            variant="outlined"
            disabled={sending}
            error={status === "error" && !subject}
          />
          <TextField
            fullWidth
            multiline
            inputRef={startRef}
            rows={8}
            label="Message"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            variant="outlined"
            disabled={sending}
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outlined" onClick={handleClose} disabled={sending}>
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={
                sending ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <Send className="size-4" />
                )
              }
              onClick={handleSend}
              disabled={sending || status === "success"}
            >
              {sending ? "Sending..." : "Send"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
